import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  listAgentTicketsQuery,
  listDeskTicketsQuery,
  listFinishedTicketsQuery,
  listPendingTicketsQuery,
} from '@/queries/ticket.query'
import { TicketService } from '@/services/ticket.service'
import {
  selectClearTicket,
  selectCurrentTicketId,
  selectFocusTicket,
  selectSetActiveTab,
  useChatStore,
} from '@/stores/chat.store'
import { IconMessageCircle } from '@tabler/icons-react'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import {
  canTakeOverTicket,
  getCurrentChatTicket,
  isHumanInProgressTicket,
} from '../utils/ticket-routing'
import { ChatEmptyState } from './chat-empty-state'
import { ChatHeader } from './chat-header'
import { ChatInput, ChatInputRef } from './chat-input'
import { ChatMessages } from './chat-messages'

export function ChatMain() {
  const currentTicketId = useChatStore(selectCurrentTicketId)
  const setActiveTab = useChatStore(selectSetActiveTab)
  const focusTicket = useChatStore(selectFocusTicket)
  const clearTicket = useChatStore(selectClearTicket)
  const queryClient = useQueryClient()
  const inputRef = useRef<ChatInputRef>(null)
  const { data: agentTickets } = useSuspenseQuery(listAgentTicketsQuery)
  const { data: deskTickets } = useSuspenseQuery(listDeskTicketsQuery)
  const { data: pendingTickets } = useSuspenseQuery(listPendingTicketsQuery)
  const { data: finishedTickets } = useSuspenseQuery(listFinishedTicketsQuery)

  const currentTicket = getCurrentChatTicket(
    currentTicketId,
    agentTickets,
    deskTickets,
    pendingTickets,
    finishedTickets
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && currentTicketId) {
        const target = event.target as HTMLElement
        const isInputFocused =
          target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'
        const hasOpenDialog = document.querySelector('[role="dialog"]') !== null
        const hasOpenLightbox = document.querySelector('.yarl__root') !== null

        if (!isInputFocused && !hasOpenDialog && !hasOpenLightbox) {
          clearTicket()
        }
      }
    }

    globalThis.addEventListener('keydown', handleKeyDown)
    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown)
    }
  }, [currentTicketId, clearTicket])

  const handleAnswerTicket = () => {
    toast.promise(TicketService.joinChat(currentTicket!.id), {
      loading: `Entrando no chat #${currentTicket?.id} de ${currentTicket?.contact.name}...`,
      success: (data) => {
        setActiveTab('atendendo')
        focusTicket(data.id)
        inputRef.current?.focus()
        return `Atendendo o chat #${currentTicket?.id} de ${currentTicket?.contact.name}`
      },
      error: () => {
        return 'Erro ao atender o chat. Tente novamente.'
      },
      finally: () => {
        queryClient.invalidateQueries(listAgentTicketsQuery)
        queryClient.invalidateQueries(listDeskTicketsQuery)
        queryClient.invalidateQueries(listPendingTicketsQuery)
      },
    })
  }
  return currentTicket ? (
    <div
      className={cn(
        'relative flex w-full flex-1 flex-col overflow-hidden rounded-md border bg-secondary shadow-sm transition-all duration-200'
      )}
    >
      <ChatHeader />

      <div className='flex flex-1 flex-col overflow-hidden rounded-md'>
        <div className='flex-1 overflow-hidden px-2 sm:px-4'>
          <ChatMessages />
        </div>
        {isHumanInProgressTicket(currentTicket) && (
          <div className='px-2 pb-2'>
            <ChatInput ref={inputRef} />
          </div>
        )}
        {canTakeOverTicket(currentTicket) && (
          <div className='absolute bottom-4 left-0 right-0 flex justify-center'>
            <Button onClick={handleAnswerTicket} className='gap-2'>
              Atender Chat
              <IconMessageCircle size={16} />
            </Button>
          </div>
        )}
      </div>
    </div>
  ) : (
    <div className='hidden w-full flex-1 sm:flex'>
      <ChatEmptyState />
    </div>
  )
}
